import { FC, useMemo } from 'react';

import { useBreakpoint } from '@contexts/breakpoints';
import {
  Int32StringTourListFeaturesViewModel,
  TourListDatesViewModel,
  RateAmountType,
  TourListPriceViewModel,
  TourMediaBaseViewModel,
} from '@api/index';
import Price from '@components/ui/price';
import { DurationIcon, LocationPinIcon } from '@assets/icons';
import ListingItemTimezone from '@components/ui/listing/ListingItemTimeZone/ListingItemTimeZone';
import CompanyPromotionGroup from '@components/ui/listing/PromotionGroup/CompanyPromotionGroup';
import WorldLogo from '@components/ui/WorldLogo/WorldLogo';
import DiscountLabel from '@components/ui/labels/discount/DiscountLabel';
import { CompanyPromotionItemProps } from '@components/ui/listing/PromotionGroup/CompanyPromotionGroupInterface';
import { formatISOToDate } from '@utils/dateUtil';
import HighLightGroup from '@components/HighLightGroup/HighLightGroup';
import MilePriceBadge from '@uikit/milePriceBadge';
import ListingItemSlider, {
  SliderImage,
} from '@components/ui/listing/ListingItemSlider';
import { useAppRouter } from '@contexts/router';
import { Col } from '@components/formStyles';

import {
  Wrapper,
  ContentWrapper,
  CarouselWrapper,
  PriceWrapper,
  TourDetails,
  TourDetailsItem,
  TourDetailsItemFull,
  TourHeader,
  TourTitle,
  TourBoardType,
  TourBoardTypeWrapper,
  TourDivider,
  TourPromotions,
  WorldLogoWrapper,
  PriceInfoWrapper,
  DiscountWrapper,
  PriceLabel,
  TourMainHeaderInfo,
  TourListingItemDetails,
  HighLightWrapper,
  FlexEnd,
} from './styled';
import {
  getTransportationIcon,
  getCustomTransportationIcon,
} from '../../helpers/getTransportationIcon';
import { getDiscountType } from '../../helpers/getDiscountType';

export interface TourListingCardProps {
  installments?: string | null;
  label?: string;
  image?: string;
  fallBackImage?: string;
  secure: boolean;
  name?: string | null;
  boardType?: string;
  promotion?: Array<CompanyPromotionItemProps>;
  adultCount?: number;
  childCount?: number;
  nightCount: number;
  isTour?: boolean;
  originalPrice?: number;
  sellingPrice?: number;
  currency?: string | null;
  discounted?: number;
  discountRate?: number;
  discountType?: RateAmountType;
  discountCurrency?: string | null;
  departureCityName?: string | null;
  placesToGo?: string | null;
  nightStay?: number | null;
  dayStay?: number | null;
  transportionTypeName?: string | null;
  url?: string;
  dates?: TourListDatesViewModel[] | null;
  transportionTypeCode?: string | null;
  tourListing?: boolean;
  features?: Int32StringTourListFeaturesViewModel[] | null;
  milePrice?: TourListPriceViewModel['milePrice'];
  images: string[];
  videos?: TourMediaBaseViewModel[] | null;
  priorityImage?: boolean;
}

const TourListingCard: FC<TourListingCardProps> = ({
  installments,
  fallBackImage,
  secure,
  name,
  boardType,
  promotion,
  isTour,
  originalPrice,
  sellingPrice,
  currency,
  discounted,
  discountRate,
  discountType,
  discountCurrency,
  departureCityName,
  nightStay,
  dayStay,
  transportionTypeName,
  transportionTypeCode,
  dates,
  url,
  tourListing,
  features,
  milePrice,
  images,
  videos,
  priorityImage,
}) => {
  const { xl } = useBreakpoint();
  const { push } = useAppRouter();
  const firstDepartureDate = dates?.[0]?.departureDate;
  const departureDateText = firstDepartureDate
    ? `${formatISOToDate(firstDepartureDate)}'den itibaren`
    : '';
  const highlights =
    features
      ?.map((item) => item?.value)
      .filter((item): item is string => Boolean(item))
      .slice(0, 3) ?? [];
  const hasPromotion = Array.isArray(promotion) && promotion.length > 0;
  const hasDiscountRate = Boolean(discountRate);
  const displayPrice = discounted ?? sellingPrice ?? originalPrice;
  const hasPrice = Boolean(displayPrice);
  const hasStayInfo =
    Number.isFinite(nightStay) &&
    Number.isFinite(dayStay) &&
    nightStay !== null &&
    dayStay !== null;
  const showPriceColumn =
    Boolean(installments) || hasDiscountRate || hasPrice || Boolean(milePrice);

  const handleNavigation = () => push(url || '');

  // Kart tıklamasında gerçekten navigate yapılmalı mı kontrol et
  const shouldSkipNavigation = (e: React.MouseEvent) => {
    if (typeof window !== 'undefined' && window.getSelection()?.toString()) {
      return true;
    }
    const target = e.target as HTMLElement | null;
    if (!target) return false;

    const interactiveTags = [
      'BUTTON',
      'A',
      'INPUT',
      'TEXTAREA',
      'SELECT',
      'LABEL',
      'SVG',
      'PATH',
    ];
    if (interactiveTags.includes(target.tagName)) return true;
    // Slider / ok / özel bileşen gibi hover / click aksiyonunu korumak için
    if (target.closest('[data-no-card-nav]')) return true;
    // Content içindeki bir ikon veya arrow componentinin içinde olabilir
    if (target.getAttribute('role') === 'button') return true;
    return false;
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (shouldSkipNavigation(e)) return; // Navigate'i engelle
    handleNavigation();
  };

  // ======================================
  // Generate the tour card slider images
  // ======================================
  const itemImages = useMemo(() => {
    const items: SliderImage[] = [];
    images?.forEach((item) => items.push({ imageUrl: item, alt: 'setur' }));

    return items;
  }, [images]);

  const videoAll = useMemo(() => {
    const tempVideos: string[] = [];
    videos?.map((item) => {
      if (item.url) tempVideos.push(item.url);
    });
    return tempVideos;
  }, [videos]);

  return (
    <Wrapper onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <CarouselWrapper data-no-card-nav>
        <ListingItemSlider
          images={itemImages || []}
          secure={secure}
          fallBackImage={fallBackImage}
          videos={videoAll}
          onImageClick={handleNavigation}
          lazyLoad={priorityImage ? undefined : 'ondemand'}
          priority={priorityImage}
        />
      </CarouselWrapper>

      <ContentWrapper>
        <TourHeader>
          <TourTitle>{name}</TourTitle>

          <TourMainHeaderInfo>
            <TourDetails>
              {departureCityName ? (
                <TourDetailsItem>
                  <ListingItemTimezone
                    icon={LocationPinIcon}
                    text={departureCityName}
                    iconSize={12}
                    iconColor='grey800'
                    textColor='grey800'
                  />
                </TourDetailsItem>
              ) : null}

              {transportionTypeName ? (
                <TourDetailsItem>
                  <ListingItemTimezone
                    icon={getTransportationIcon(transportionTypeCode)}
                    text={transportionTypeName}
                    iconSize={12}
                    iconColor='grey800'
                    textColor='grey800'
                    CustomIconComponent={() =>
                      getCustomTransportationIcon(transportionTypeCode)
                    }
                  />
                </TourDetailsItem>
              ) : null}

              {hasStayInfo ? (
                <TourDetailsItem>
                  <ListingItemTimezone
                    icon={DurationIcon}
                    text={`${nightStay} Gece, ${dayStay} Gün`}
                    iconSize={12}
                    iconColor='grey800'
                    textColor='grey800'
                  />
                </TourDetailsItem>
              ) : null}

              {departureDateText ? (
                <TourDetailsItemFull>
                  <ListingItemTimezone
                    text={departureDateText}
                    iconSize={12}
                    iconColor='grey800'
                    textColor='grey800'
                    CustomIconComponent={
                      !xl ? undefined : () => <TourBoardType> | </TourBoardType>
                    }
                  />
                </TourDetailsItemFull>
              ) : null}
            </TourDetails>
            {departureDateText ? (
              <TourBoardTypeWrapper>
                <TourBoardType />
              </TourBoardTypeWrapper>
            ) : null}
            <TourDivider />
          </TourMainHeaderInfo>
        </TourHeader>

        <PriceWrapper>
          <Col>
            {highlights.length > 0 ? (
              <TourListingItemDetails>
                <HighLightWrapper>
                  <HighLightGroup
                    listing={tourListing}
                    highLights={highlights}
                  />
                </HighLightWrapper>
              </TourListingItemDetails>
            ) : null}

            {xl && hasPromotion ? (
              <TourPromotions>
                <CompanyPromotionGroup promotions={promotion || []} />
              </TourPromotions>
            ) : null}
          </Col>
          {showPriceColumn ? (
            <Col>
              <WorldLogoWrapper>
                <WorldLogo label={installments} />
              </WorldLogoWrapper>
              {hasDiscountRate ? (
                <DiscountWrapper>
                  <DiscountLabel
                    discount={discountRate as number}
                    type={getDiscountType(discountType)}
                    currency={discountCurrency}
                  />
                </DiscountWrapper>
              ) : null}

              {hasPrice ? (
                <PriceInfoWrapper>
                  <FlexEnd>
                    {milePrice && <MilePriceBadge milePrice={milePrice} />}
                    <Price
                      tourListing={tourListing}
                      originalPriceCurrency={currency}
                      originalPrice={originalPrice}
                      sellingPrice={sellingPrice}
                      price={displayPrice}
                      isTour={isTour}
                      tourType={boardType}
                    />
                  </FlexEnd>
                  <PriceLabel>‘den başlayan fiyatlarla</PriceLabel>
                </PriceInfoWrapper>
              ) : null}
            </Col>
          ) : null}
        </PriceWrapper>
      </ContentWrapper>

      {!xl && hasPromotion ? (
        <TourPromotions>
          <CompanyPromotionGroup promotions={promotion || []} />
        </TourPromotions>
      ) : null}
    </Wrapper>
  );
};

export default TourListingCard;
